import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import OnlineGameMenu from './OnlineGameMenu';
import { API_URL } from './environment';
import {
  getLaunchTokenFromUrl,
  getPlayerSessionHeaders,
  getStoredPlayerSession,
  isPlayerSessionExpired,
  persistPlayerSession,
} from './onlinePlayerSession';
import './OnlineInfoPage.css';

const payTableRows = [
  { bet: 'Straight/Single', minBet: 20, maxBet: 2000, payout: '35:1' },
  { bet: 'Split', minBet: 20, maxBet: 2000, payout: '17:1' },
  { bet: 'Street', minBet: 20, maxBet: 2000, payout: '11:1' },
  { bet: 'Corner', minBet: 20, maxBet: 2000, payout: '8:1' },
  { bet: 'Six line', minBet: 20, maxBet: 2000, payout: '5:1' },
  { bet: 'Column', minBet: 20, maxBet: 5000, payout: '2:1' },
  { bet: 'Dozen', minBet: 20, maxBet: 5000, payout: '2:1' },
  { bet: 'Red/Black', minBet: 20, maxBet: 5000, payout: '1:1' },
  { bet: 'Even/Odd', minBet: 20, maxBet: 5000, payout: '1:1' },
  { bet: <>High/Low<br />(1-18/19-36)</>, minBet: 20, maxBet: 5000, payout: '1:1' },
];

const tableMaxBetRule = 'The max bet for the roulette table is set at 30000';

const rulesBetRows = [
  {
    bet: 'Straight',
    description: 'Bet on a single number (0-36).',
    payout: '35:1',
  },
  {
    bet: 'Split',
    description: 'Bet on two adjacent numbers.',
    payout: '17:1',
  },
  {
    bet: 'Street',
    description: 'Bet on a row of three numbers.',
    payout: '11:1',
  },
  {
    bet: 'Corner',
    description: 'Bet on four adjoining numbers.',
    payout: '8:1',
  },
  {
    bet: 'Six-Line',
    description: 'Bet on two adjacent rows, covering six numbers.',
    payout: '5:1',
  },
  {
    bet: 'Column',
    description: 'Bet on Column 1, Column 2, or Column 3, covering 12 numbers.',
    payout: '2:1',
  },
  {
    bet: 'Dozen',
    description: 'Bet on the 1st 12 (1-12), 2nd 12 (13-24), or 3rd 12 (25-36).',
    payout: '2:1',
  },
  {
    bet: 'Red / Black',
    description: 'Bet that the winning number will be Red or Black. Covers 18 numbers.',
    payout: '1:1',
  },
  {
    bet: 'Even / Odd',
    description: 'Bet that the winning number will be Even or Odd. Covers 18 numbers.',
    payout: '1:1',
  },
  {
    bet: 'High / Low',
    description: 'Bet on Low (1-18) or High (19-36). Covers 18 numbers.',
    payout: '1:1',
  },
];

const renderRulesContent = (launchQueryString) => (
  <div className="online-rules-content">
    <section className="online-rules-section">
      <h2>Welcome to Single-Zero Roulette</h2>
      <p>
        Experience the excitement of <strong>Single-Zero Roulette</strong>, where every spin gives you a new opportunity to win. This is a <strong>wide-area network game</strong>, meaning every player on the platform participates in the same live game round, creating a shared and exciting experience.
      </p>
      <p>
        Every <strong>3 minutes</strong>, the roulette wheel spins and randomly selects one of <strong>37 pockets</strong>, numbered <strong>0 to 36</strong>. The wheel features alternating <strong>Red and Black</strong> numbers, while <strong>0</strong> is the only Green pocket.
      </p>
      <p>
        Your objective is simple: correctly predict where the roulette ball will land. If your selection matches the winning outcome, your bet is paid according to the official payout table.
      </p>
    </section>

    <section className="online-rules-section">
      <h2>How to Play</h2>
      <p>
        Before each spin, place one or more bets on the betting table using your preferred chip denominations. You may combine different bet types, provided your total stake for each bet does not exceed the maximum limit for that wager.
      </p>
      <p>
        Once betting closes, the wheel spins automatically. If your prediction is correct, your winnings are credited based on the payout for the winning bet.
      </p>
    </section>

    <section className="online-rules-section">
      <h2>Common Bet Types</h2>
      <div className="online-rules-table-wrap">
        <table className="online-rules-table" aria-label="Common roulette bet types">
          <thead>
            <tr>
              <th>Bet Type</th>
              <th>Description</th>
              <th>Payout</th>
            </tr>
          </thead>
          <tbody>
            {rulesBetRows.map((row) => (
              <tr key={row.bet}>
                <td>{row.bet}</td>
                <td>{row.description}</td>
                <td>{row.payout}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    <section className="online-rules-section">
      <h2>Important Information</h2>
      <ul className="online-rules-list">
        <li>Betting is available only while the betting window is open.</li>
        <li>Multiple bets may be placed on the same spin.</li>
        <li>Maximum bet limits apply to each wager type.</li>
        <li>All winning bets are paid according to the official pay table.</li>
      </ul>
      <p>
        Please refer to the <Link to={`/paytable${launchQueryString}`} className="online-rules-link"><strong>Pay Table</strong></Link> within the game for the complete list of payouts, maximum bet limits, and any applicable game rules.
      </p>
    </section>
  </div>
);

function OnlineInfoPage({ title }) {
  const pageRef = useRef(null);
  const isPayTable = title === 'Pay Table';
  const isRules = title === 'Rules';
  const displayTitle = isRules ? 'Game Rules' : title;
  const pageDescription = isPayTable
    ? 'Bet limits and payouts for every available wager.'
    : 'Everything you need to play single-zero roulette with confidence.';
  const launchToken = getLaunchTokenFromUrl();
  const launchQueryString = launchToken ? `?launchToken=${encodeURIComponent(launchToken)}` : '';

  useEffect(() => {
    document.body.classList.add('online-info-active');

    return () => {
      document.body.classList.remove('online-info-active');
    };
  }, []);

  useEffect(() => {
    if (pageRef.current) {
      pageRef.current.scrollTo({ top: 0, left: 0 });
    }
  }, [title]);

  useEffect(() => {
    const cachedSession = getStoredPlayerSession();

    if (
      !cachedSession?.tokenHash ||
      cachedSession.launchToken !== launchToken ||
      isPlayerSessionExpired(cachedSession)
    ) {
      return;
    }

    axios.get(`${API_URL}/playersessions/me`, {
      headers: getPlayerSessionHeaders(cachedSession),
      timeout: 8000,
    }).then((response) => {
      const data = response?.data || {};

      persistPlayerSession({
        ...cachedSession,
        tokenHash: data.tokenHash || cachedSession.tokenHash,
        playerSessionId: data.playerSessionId || data.playerSession?._id || cachedSession.playerSessionId,
        expiredAt: data.expiredAt || data.session?.expiredAt || cachedSession.expiredAt,
        balance: data.balance ?? data.playerBalance ?? cachedSession.balance,
        player: data.player || cachedSession.player,
        shop: data.bettingShop || data.shop || cachedSession.shop,
        raw: data,
      });
    }).catch(() => {
      // Rules and pay table remain available while offline; the game retries later.
    });
  }, [launchToken]);

  return (
    <main ref={pageRef} className={`online-info-page ${isRules ? 'online-info-page--rules' : ''}`}>
      <section className={`online-info-card ${isPayTable ? 'online-info-card--paytable' : ''} ${isRules ? 'online-info-card--rules' : ''}`}>
        <header className="online-info-heading">
          <div className="online-info-heading__eyebrow-row">
            <OnlineGameMenu />
            <span>Online roulette</span>
          </div>
          <h1>{displayTitle}</h1>
          <p>{pageDescription}</p>
        </header>
        {isPayTable && (
          <div className="online-paytable-wrap">
            <table className="online-paytable" aria-label="Roulette pay table">
              <thead>
                <tr>
                  <th>Bet</th>
                  <th>Min bet</th>
                  <th>Max bet</th>
                  <th>Payouts</th>
                </tr>
              </thead>
              <tbody>
                {payTableRows.map((row, index) => (
                  <tr key={index}>
                    <td data-label="Bet">{row.bet}</td>
                    <td data-label="Min bet">{row.minBet}</td>
                    <td data-label="Max bet">{row.maxBet}</td>
                    <td data-label="Payouts">{row.payout}</td>
                  </tr>
                ))}
                <tr className="online-paytable-rule-row">
                  <td colSpan="4">{tableMaxBetRule}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        {isRules && renderRulesContent(launchQueryString)}
      </section>
    </main>
  );
}

export default OnlineInfoPage;
